import { useContext, useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Box, Typography, LinearProgress, Collapse, Button, Chip, Stack } from "@mui/material";

import { ChatContext } from "../../contexts/chat/context";
import { AtomicLogo } from "../../assets/AtomicLogo";
import { CHAT_FONT_STACK } from "../../contexts/material_ui/theme";
import { NullStateMessage } from "../../components/layout/NullStateMessage";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { ChatStatus } from "../../components/chat/chat_status";
import { InputContainer } from "../../components/chat/input_container";
import { SQLDisplay } from "../../components/chat/sql_display";
import { ChartDisplay } from "../../components/chat/chart_display";
import { GenericTableDisplay } from "../../components/chat/generic_table_display";
import {
  TableData,
  Message,
  DocSearch,
  SQLQuery,
  ChartGenerated,
  ReasoningStep,
  ToolCall,
} from "../../contexts/chat/type";
import { ToolCallsContainer } from "../../components/chat/tool_calls_container";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Animated dots component for loading states
const AnimatedDots = () => {
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span style={{ display: "inline-block", width: "1.5em" }}>{dots}</span>
  );
};

// Wave dots component (like iMessage typing indicator)
const WaveDots = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "3px",
      "& span": {
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "primary.main",
        animation: "wave 1.2s ease-in-out infinite",
      },
      "& span:nth-of-type(2)": {
        animationDelay: "0.2s",
      },
      "& span:nth-of-type(3)": {
        animationDelay: "0.4s",
      },
      "@keyframes wave": {
        "0%, 60%, 100%": {
          transform: "translateY(0)",
          opacity: 0.4,
        },
        "30%": {
          transform: "translateY(-4px)",
          opacity: 1,
        },
      },
    }}
  >
    <span />
    <span />
    <span />
  </Box>
);

// Format timestamp as "H:MM AM/PM, Mon DD" in user's timezone
const formatMessageTimestamp = (timestamp: Date): string => {
  const date = new Date(timestamp);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 should be 12
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const day = date.getDate();
  return `${hours}:${minutes} ${ampm}, ${month} ${day}`;
};

// DataTable component for rendering structured table data
const DataTable = ({ table }: { table: TableData }) => {
  return (
    <Box sx={{ my: 2 }}>
      {table.name && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {table.name}
        </Typography>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
            minWidth: "max-content",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr>
              {table.headers.map((header, i) => (
                <th
                  key={i}
                  style={{
                    border: "1px solid #ddd",
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    style={{
                      border: "1px solid #ddd",
                      padding: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {table.summary && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {table.summary}
        </Typography>
      )}
    </Box>
  );
};

// Custom animated waiting component (fallback when not streaming)
const CustomAwaitingResponse = () => {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {Array.from({ length: dotCount }).map((_, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: "grey.500",
            height: "8px",
            width: "8px",
            borderRadius: "50%",
            mr: 0.25,
          }}
        />
      ))}
    </Box>
  );
};

// SQL Query Card - expandable with results table
const SqlQueryCard = ({ query }: { query: SQLQuery }) => {
  const [showResults, setShowResults] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 1,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "success.dark", p: 1, pb: 0.5 }}
      >
        🔍 SQL Query - {query.row_count} rows
      </Typography>
      <Box
        sx={{
          backgroundColor: "grey.900",
          p: 1.5,
          overflow: "auto",
          maxHeight: 120,
        }}
      >
        <Typography
          component="pre"
          sx={{
            fontSize: "0.8rem",
            fontFamily: "monospace",
            color: "grey.100",
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {query.sql || "SQL not available"}
        </Typography>
      </Box>
      {/* Expandable results table */}
      {query.results &&
        query.results.length > 0 &&
        query.columns &&
        query.columns.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "success.main",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setShowResults(!showResults)}
            >
              {showResults ? "▼ Hide" : "▶ View"} Results (
              {query.results.length} of {query.row_count} rows)
            </Typography>
            <Collapse in={showResults}>
              <Box
                sx={{
                  mt: 1,
                  maxHeight: 200,
                  overflow: "auto",
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.75rem",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#e8e8e8",
                        position: "sticky",
                        top: 0,
                      }}
                    >
                      {query.columns.map((col, idx) => (
                        <th
                          key={idx}
                          style={{
                            padding: "6px 8px",
                            textAlign: "left",
                            borderBottom: "1px solid #ddd",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {query.results.slice(0, 20).map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{ borderBottom: "1px solid #eee" }}
                      >
                        {query.columns!.map((col, colIdx) => (
                          <td
                            key={colIdx}
                            style={{ padding: "4px 8px", whiteSpace: "nowrap" }}
                          >
                            {row[col] !== null && row[col] !== undefined ? (
                              String(row[col])
                            ) : (
                              <span
                                style={{ color: "#999", fontStyle: "italic" }}
                              >
                                null
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Collapse>
          </Box>
        )}
    </Box>
  );
};

// Doc Search Card - expandable with documentation response
const DocSearchCard = ({ search }: { search: DocSearch }) => {
  const [showDocs, setShowDocs] = useState(false);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 1,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        p: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "secondary.dark", mb: 0.5 }}
      >
        📚 Doc Search
      </Typography>
      <Box sx={{ backgroundColor: "grey.100", p: 1, borderRadius: 1, mb: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.primary", fontStyle: "italic" }}
        >
          "{search.question}"
        </Typography>
      </Box>
      {/* Table names found */}
      {search.table_names && search.table_names.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          {search.table_names.map((table, tableIdx) => (
            <Box
              key={tableIdx}
              sx={{
                backgroundColor: "white",
                border: "1px solid",
                borderColor: "secondary.main",
                color: "secondary.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontFamily: "monospace",
                fontWeight: 500,
              }}
            >
              {table}
            </Box>
          ))}
        </Box>
      )}
      {/* Expandable documentation response */}
      {search.schema_info && (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: "secondary.main",
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
            onClick={() => setShowDocs(!showDocs)}
          >
            {showDocs ? "▼ Hide" : "▶ View"} Documentation Response
          </Typography>
          <Collapse in={showDocs}>
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: "grey.50",
                borderRadius: 1,
                maxHeight: 200,
                overflow: "auto",
              }}
            >
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  m: 0,
                  fontSize: "0.75rem",
                }}
              >
                {search.schema_info}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

// Unified AgentTrace component - shows during streaming (expanded) and after completion (collapsed)
interface AgentTraceProps {
  // For streaming mode - uses context
  isStreaming?: boolean;
  // For completed message mode - uses message data
  message?: Message;
}

const EMPTY_SQL_QUERIES: SQLQuery[] = [];
const EMPTY_DOC_SEARCHES: DocSearch[] = [];
const EMPTY_CHARTS: ChartGenerated[] = [];
const EMPTY_REASONING_HISTORY: ReasoningStep[] = [];
const EMPTY_TOOL_CALLS: ToolCall[] = [];

const AgentTraceDisplay = ({
  isStreaming = false,
  message,
}: AgentTraceProps) => {
  const { agentState } = useContext(ChatContext);

  // Auto-expand during streaming, collapsed for completed messages
  const [showDetails, setShowDetails] = useState(isStreaming);

  // Auto-expand when streaming starts, auto-collapse when it ends
  useEffect(() => {
    if (isStreaming) {
      setShowDetails(true);
    }
  }, [isStreaming]);

  // Get data from either streaming state or completed message
  const sqlQueries: SQLQuery[] = isStreaming
    ? agentState.sqlQueries
    : message?.sqlQueries ?? EMPTY_SQL_QUERIES;
  const docSearches: DocSearch[] = isStreaming
    ? agentState.docSearches
    : message?.docSearches ?? EMPTY_DOC_SEARCHES;
  const charts: ChartGenerated[] = isStreaming
    ? agentState.charts
    : message?.charts ?? EMPTY_CHARTS;
  const reasoningHistory: ReasoningStep[] = isStreaming
    ? agentState.reasoningHistory
    : message?.reasoningHistory ?? EMPTY_REASONING_HISTORY;
  const toolCalls: ToolCall[] = isStreaming
    ? agentState.toolCalls
    : message?.toolCalls ?? EMPTY_TOOL_CALLS;
  const currentIteration = isStreaming
    ? agentState.currentIteration
    : message?.iterationCount || 0;
  const maxIterations = isStreaming
    ? agentState.maxIterations
    : message?.iterationCount || 0;
  const currentReasoning = isStreaming ? agentState.reasoning : "";

  // Combine and sort all trace events by iteration number
  type TraceEvent =
    | { type: "sql"; iteration: number; data: SQLQuery }
    | { type: "doc"; iteration: number; data: DocSearch }
    | { type: "reasoning"; iteration: number; data: ReasoningStep };

  const sortedTraceEvents: TraceEvent[] = useMemo(() => {
    const events: TraceEvent[] = [
      ...sqlQueries.map(
        (q): TraceEvent => ({ type: "sql", iteration: q.iteration, data: q })
      ),
      ...docSearches.map(
        (d): TraceEvent => ({ type: "doc", iteration: d.iteration, data: d })
      ),
      ...reasoningHistory.map(
        (r): TraceEvent => ({
          type: "reasoning",
          iteration: r.iteration,
          data: r,
        })
      ),
    ];
    return events.sort((a, b) => a.iteration - b.iteration);
  }, [sqlQueries, docSearches, reasoningHistory]);

  // Check if there's any trace data to show
  const hasTraceData =
    sqlQueries.length > 0 ||
    docSearches.length > 0 ||
    charts.length > 0 ||
    reasoningHistory.length > 0 ||
    toolCalls.length > 0;

  // For completed messages, only show if there's trace data
  if (!isStreaming && !hasTraceData) {
    return null;
  }

  const progress =
    maxIterations > 0 ? (currentIteration / maxIterations) * 100 : 0;

  // Wrapper - full box for streaming, inline section for completed
  const content = (
    <>
      {/* Header - different for streaming vs completed */}
      {isStreaming ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WaveDots />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                Thinking
                <AnimatedDots />
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mb: 1.5,
              height: 4,
              borderRadius: 2,
              backgroundColor: "grey.200",
            }}
          />
        </>
      ) : (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            cursor: "pointer",
            mb: showDetails ? 1 : 0,
            px: 2,
            py: 1,
            borderRadius: "20px",
            // Glassmorphism style
            background: "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.4)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.8)",
              boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)",
              transform: "translateY(-1px)",
            },
          }}
          onClick={() => setShowDetails(!showDetails)}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              transition: "transform 0.2s ease",
              transform: showDetails ? "rotate(90deg)" : "rotate(0deg)",
              fontSize: "0.7rem",
              color: "primary.main",
            }}
          >
            ▶
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: "text.secondary",
              fontFamily: CHAT_FONT_STACK,
              fontSize: "0.85rem",
            }}
          >
            Thinking trace
          </Typography>
        </Box>
      )}

      {/* Tool Calls - show during streaming and in completed trace */}
      {toolCalls.length > 0 && (
        <ToolCallsContainer
          toolCalls={toolCalls}
          isStreaming={isStreaming}
        />
      )}

      {/* Current reasoning - only during streaming */}
      {isStreaming && currentReasoning && (
        <Box
          sx={{ backgroundColor: "grey.50", p: 2, borderRadius: 1, mb: 1.5 }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontStyle: "italic",
              fontSize: "0.9rem",
              lineHeight: 1.6,
            }}
          >
            {currentReasoning}
          </Typography>
        </Box>
      )}

      {/* Activity indicators - white background with colored border */}
      {(isStreaming || showDetails) && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            mb: hasTraceData ? 1 : 0,
          }}
        >
          {docSearches.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "secondary.main",
                color: "secondary.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>📚</span>
              <span>
                {docSearches.length} doc search
                {docSearches.length > 1 ? "es" : ""}
              </span>
            </Box>
          )}
          {sqlQueries.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "success.main",
                color: "success.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>🔍</span>
              <span>
                {sqlQueries.length} SQL quer
                {sqlQueries.length > 1 ? "ies" : "y"}
              </span>
            </Box>
          )}
          {charts.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "warning.main",
                color: "warning.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>📊</span>
              <span>
                {charts.length} chart{charts.length > 1 ? "s" : ""}
              </span>
            </Box>
          )}
          {toolCalls.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: "white",
                border: "1.5px solid",
                borderColor: "info.main",
                color: "info.dark",
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.75rem",
              }}
            >
              <span>🔧</span>
              <span>
                {toolCalls.length} tool{toolCalls.length > 1 ? "s" : ""}
              </span>
            </Box>
          )}
        </Box>
      )}

      {/* Expandable details */}
      {hasTraceData && (
        <Box sx={{ mt: isStreaming ? 1.5 : 0 }}>
          {isStreaming && (
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Hide details" : "Show details"}
            </Typography>
          )}
          <Collapse in={showDetails}>
            <Box sx={{ mt: 1.5, position: "relative", pl: 3 }}>
              <Box
                sx={{
                  position: "absolute",
                  left: 6,
                  top: 8,
                  bottom: 8,
                  width: 2,
                  backgroundColor: "grey.200",
                }}
              />
              {sortedTraceEvents.map((event, idx) => {
                const isLast = idx === sortedTraceEvents.length - 1;
                const dotColor =
                  event.type === "sql"
                    ? "success.main"
                    : event.type === "doc"
                      ? "secondary.main"
                      : "primary.main";

                if (event.type === "sql") {
                  return (
                    <Box
                      key={`sql-${event.iteration}-${idx}`}
                      sx={{
                        mb: isLast ? 0 : 2,
                        position: "relative",
                        animation: "fadeIn 0.3s ease-out",
                        "@keyframes fadeIn": {
                          from: { opacity: 0, transform: "translateY(-8px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: -24,
                          top: 4,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: dotColor,
                          border: "2px solid white",
                          boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                        }}
                      />
                      <SqlQueryCard query={event.data} />
                    </Box>
                  );
                } else if (event.type === "doc") {
                  return (
                    <Box
                      key={`doc-${event.iteration}-${idx}`}
                      sx={{
                        mb: isLast ? 0 : 2,
                        position: "relative",
                        animation: "fadeIn 0.3s ease-out",
                        "@keyframes fadeIn": {
                          from: { opacity: 0, transform: "translateY(-8px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: -24,
                          top: 4,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: dotColor,
                          border: "2px solid white",
                          boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                        }}
                      />
                      <DocSearchCard search={event.data} />
                    </Box>
                  );
                } else {
                  const reasoning = event.data;
                  return (
                    <Box
                      key={`reasoning-${event.iteration}-${idx}`}
                      sx={{
                        mb: isLast ? 0 : 2,
                        position: "relative",
                        animation: "fadeIn 0.3s ease-out",
                        "@keyframes fadeIn": {
                          from: { opacity: 0, transform: "translateY(-8px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: -24,
                          top: 4,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: dotColor,
                          border: "2px solid white",
                          boxShadow: "0 0 0 2px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Box
                        sx={{
                          backgroundColor: "white",
                          borderRadius: 1,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          p: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "primary.dark",
                            mb: 0.5,
                          }}
                        >
                          💡 Thinking...
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontStyle: "italic",
                            lineHeight: 1.5,
                          }}
                        >
                          {reasoning.text}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              })}
            </Box>
          </Collapse>
        </Box>
      )}
    </>
  );

  // For streaming, wrap in a floating card
  if (isStreaming) {
    return (
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
        <Box
          sx={{
            maxWidth: "85%",
            minWidth: "320px",
            p: 2.5,
            borderRadius: "20px",
            background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow:
              "0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 10px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(25, 118, 210, 0.1)",
            // Fade + float up animation
            animation: "floatIn 0.4s ease-out",
            "@keyframes floatIn": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  // For completed messages, render inline
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
      {content}
    </Box>
  );
};

// Example prompts that rotate on the welcome screen
const EXAMPLE_PROMPTS = [
  "what changed since the last run?",
  "what's the revenue impact of the latest run?",
  "how did inventory dollars change between runs?",
  "which skus are at risk of stocking out?",
  "what were my top sellers last month?",
];

const CAPABILITY_PILLS = [
  "Run-aware answers",
  "Tool-backed diagnosis",
  "SQL-backed tables",
  "Traceable reasoning",
  "Docs + metrics context",
];

const QUICK_ACTIONS: Array<{ label: string; prompt: string }> = [
  {
    label: "Top At-Risk SKUs",
    prompt: "Show top 5 at-risk SKU/location pairs for the next 90 days with severity, days-until-risk, and unfulfilled demand.",
  },
  {
    label: "Diagnose One SKU",
    prompt: "Find the most critical at-risk SKU/location, then diagnose shortfall and return root cause, stockout timing, and recommended next action.",
  },
  {
    label: "Latest Run Delta",
    prompt: "Compare latest run vs previous run and summarize the top 5 changes that need action first.",
  },
  {
    label: "Supply Walk View",
    prompt: "In the latest Supply Walk view, show SKU/location rows where starting weeks of supply is below 4 (lowest first, limit 20), then summarize the biggest hotspots.",
  },
  {
    label: "Data Setup",
    prompt: "From documentation, explain this workspace's data setup: major transformation stages, key assumptions, and how planners should interpret outputs.",
  },
];

const QuickActionRow = ({
  compact = false,
  disabled = false,
  onSelect,
}: {
  compact?: boolean;
  disabled?: boolean;
  onSelect: (prompt: string) => void;
}) => {
  const actions = compact ? QUICK_ACTIONS.slice(0, 4) : QUICK_ACTIONS;

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
      {actions.map((action) => (
        <Chip
          key={action.label}
          clickable={!disabled}
          disabled={disabled}
          label={action.label}
          onClick={() => onSelect(action.prompt)}
          sx={{
            borderRadius: "999px",
            border: "1px solid",
            borderColor: "rgba(25, 118, 210, 0.25)",
            backgroundColor: "rgba(25, 118, 210, 0.06)",
            color: "primary.dark",
            fontWeight: 500,
            height: 34,
            "& .MuiChip-label": {
              px: 1.25,
            },
          }}
        />
      ))}
    </Stack>
  );
};

const CapabilityPanel = ({
  disabled = false,
  onSelectQuickAction,
}: {
  disabled?: boolean;
  onSelectQuickAction: (prompt: string) => void;
}) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: 760,
      mt: 2.5,
      p: 2,
      borderRadius: "16px",
      border: "1px solid",
      borderColor: "rgba(25, 118, 210, 0.2)",
      background:
        "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0.95) 55%)",
    }}
  >
    <Typography
      sx={{
        fontFamily: CHAT_FONT_STACK,
        fontWeight: 600,
        fontSize: "0.95rem",
        mb: 1,
      }}
    >
      What Nucleus can do for planners
    </Typography>
    <Stack
      direction="row"
      spacing={0.8}
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 1.5 }}
    >
      {CAPABILITY_PILLS.map((pill) => (
        <Chip
          key={pill}
          label={pill}
          size="small"
          sx={{
            backgroundColor: "white",
            border: "1px solid",
            borderColor: "rgba(15, 23, 42, 0.12)",
          }}
        />
      ))}
    </Stack>
    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", mb: 1 }}>
      Pick a quick action:
    </Typography>
    <QuickActionRow disabled={disabled} onSelect={onSelectQuickAction} />
  </Box>
);

// Shuffle array using Fisher-Yates algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const WelcomeScreen = () => {
  const { sendMessage, isAwaitingResponse, agentState } = useContext(ChatContext);
  const [shuffledPrompts] = useState(() => shuffleArray(EXAMPLE_PROMPTS));
  const [displayedPrompt, setDisplayedPrompt] = useState("");
  const [promptIndex, setPromptIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [animationStarted, setAnimationStarted] = useState(false);
  const quickActionsDisabled =
    isAwaitingResponse || agentState.status === "streaming";

  // Start the prompt animation after initial animations complete
  useEffect(() => {
    const startDelay = setTimeout(() => {
      setAnimationStarted(true);
    }, 2200); // Start after Nucleus typewriter + small pause
    return () => clearTimeout(startDelay);
  }, []);

  // Handle typing and erasing animation
  useEffect(() => {
    if (!animationStarted) return;

    const currentPrompt = shuffledPrompts[promptIndex];
    let timeout: NodeJS.Timeout;

    if (isTyping) {
      if (displayedPrompt.length < currentPrompt.length) {
        // Type next character
        timeout = setTimeout(() => {
          setDisplayedPrompt(
            currentPrompt.slice(0, displayedPrompt.length + 1)
          );
        }, 50); // Typing speed
      } else {
        // Finished typing, pause then start erasing
        timeout = setTimeout(() => {
          setIsTyping(false);
        }, 3000); // Pause duration
      }
    } else {
      if (displayedPrompt.length > 0) {
        // Erase character
        timeout = setTimeout(() => {
          setDisplayedPrompt(displayedPrompt.slice(0, -1));
        }, 30); // Erasing speed (faster than typing)
      } else {
        // Finished erasing, move to next prompt
        timeout = setTimeout(() => {
          setPromptIndex((prev) => (prev + 1) % shuffledPrompts.length);
          setIsTyping(true);
        }, 300); // Small pause before next prompt
      }
    }

    return () => clearTimeout(timeout);
  }, [animationStarted, displayedPrompt, promptIndex, isTyping, shuffledPrompts]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        pb: 12,
        minHeight: 0,
      }}
    >
      {/* Branding */}
      <Box sx={{ textAlign: "center", mb: 1 }}>
        {/* Logo with spin-in animation, then continuous rotation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1,
            opacity: 0,
            animation:
              "spinIn 0.7s ease-out forwards, continuousSpin 8s linear infinite",
            animationDelay: "0s, 0.7s",
            "@keyframes spinIn": {
              from: {
                opacity: 0,
                transform: "rotate(-180deg) scale(0.5)",
              },
              to: {
                opacity: 1,
                transform: "rotate(0deg) scale(1)",
              },
            },
            "@keyframes continuousSpin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        >
          <AtomicLogo width={48} height={48} />
        </Box>
        {/* Nucleus text with typewriter animation */}
        <Typography
          sx={{
            fontFamily: CHAT_FONT_STACK,
            fontSize: "2rem",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            color: "text.primary",
            overflow: "hidden",
            whiteSpace: "nowrap",
            width: "0ch",
            animation: "typewriter 1s steps(7) forwards",
            animationDelay: "0.7s",
            "@keyframes typewriter": {
              from: { width: "0ch" },
              to: { width: "7ch" },
            },
          }}
        >
          Nucleus
        </Typography>
      </Box>

      {/* Rotating example prompts - separate from Nucleus to prevent layout shift */}
      <Box
        sx={{
          height: "28px",
          width: "100%",
          maxWidth: 600,
          textAlign: "center",
          mb: 2,
          opacity: 0,
          animation: "fadeIn 0.5s ease-out forwards",
          animationDelay: "2s",
          "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: CHAT_FONT_STACK,
            fontSize: "1rem",
            color: "text.secondary",
            display: "inline",
          }}
        >
          {displayedPrompt}
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "2px",
            height: "1em",
            backgroundColor: "primary.main",
            marginLeft: "2px",
            verticalAlign: "text-bottom",
            animation: "cursorBlink 0.7s step-end infinite",
            "@keyframes cursorBlink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0 },
            },
          }}
        />
      </Box>

      {/* Centered Input with slide-up animation */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          opacity: 1,
          transform: "none",
        }}
      >
        <InputContainer />
      </Box>

      <CapabilityPanel
        disabled={quickActionsDisabled}
        onSelectQuickAction={sendMessage}
      />
    </Box>
  );
};

interface CustomMessageContainerProps {
  scrollContainerRef: { current: HTMLDivElement | null };
  shouldAutoScroll: boolean;
}

// Custom message container component for full-screen chat
const CustomMessageContainer = ({
  scrollContainerRef,
  shouldAutoScroll,
}: CustomMessageContainerProps) => {
  const { messages, isAwaitingResponse, agentState } = useContext(ChatContext);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(0);

  // Auto-scroll to bottom when new messages arrive and user is at the end
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [scrollContainerRef]);

  // Scroll only when a new message is appended (not on every streaming state update)
  useEffect(() => {
    if (messages.length > previousMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      // Always show the user's own queued/sent message immediately.
      // For assistant updates, only auto-follow when user is pinned to bottom.
      if (shouldAutoScroll || lastMessage?.type === "sent") {
        scrollToBottom();
      }
    }
    previousMessageCountRef.current = messages.length;
  }, [messages, shouldAutoScroll, scrollToBottom]);

  return (
    <>
      {messages.map((message, index) => {
        const isTableMessage = message.dataType === "table" && message.tables;

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              justifyContent:
                message.type === "sent" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Box
              sx={{
                maxWidth: "70%",
                minWidth: "100px",
                p: 2,
                px: 2.5,
                borderRadius: "20px",
                // User messages: gradient bubble
                ...(message.type === "sent" && {
                  background:
                    "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                  boxShadow: "0 2px 12px rgba(25, 118, 210, 0.15)",
                }),
                // AI messages: glassmorphism effect
                ...(message.type === "received" && {
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                }),
                wordWrap: "break-word",
                overflowWrap: "break-word",
                // Fade + scale animation
                animation: "messageFadeScale 0.3s ease-out",
                "@keyframes messageFadeScale": {
                  from: {
                    opacity: 0,
                    transform: "scale(0.95)",
                  },
                  to: {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
                // Hover state for timestamp
                "&:hover .message-timestamp": {
                  opacity: 1,
                },
              }}
            >
              {isTableMessage && message.content && (
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {message.content}
                </Typography>
              )}

              {isTableMessage && message.tables ? (
                <Box sx={{ overflowX: "auto" }}>
                  {message.tables.map((table, idx) => (
                    <DataTable key={idx} table={table} />
                  ))}
                </Box>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom styling for bullet points
                    ul: ({ children }) => (
                      <Box
                        component="ul"
                        sx={{
                          margin: "0.5em 0",
                          paddingLeft: "1.5em",
                        }}
                      >
                        {children}
                      </Box>
                    ),
                    // Custom styling for ordered lists
                    ol: ({ children }) => (
                      <Box
                        component="ol"
                        sx={{
                          margin: "0.5em 0",
                          paddingLeft: "1.5em",
                        }}
                      >
                        {children}
                      </Box>
                    ),
                    // List items with consistent font size
                    li: ({ children }) => (
                      <Typography
                        component="li"
                        variant="body2"
                        sx={{
                          fontFamily: CHAT_FONT_STACK,
                          marginBottom: "0.4em",
                          lineHeight: 1.6,
                          "&:last-child": {
                            marginBottom: 0,
                          },
                        }}
                      >
                        {children}
                      </Typography>
                    ),
                    // Better paragraph spacing - same size as list items
                    p: ({ children }) => (
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{
                          fontFamily: CHAT_FONT_STACK,
                          margin: "0.5em 0",
                          lineHeight: 1.6,
                          "&:first-of-type": {
                            marginTop: 0,
                          },
                          "&:last-child": {
                            marginBottom: 0,
                          },
                        }}
                      >
                        {children}
                      </Typography>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
              {/* Display SQL queries if present */}
              {message.type === "received" &&
                message.sqlQueries &&
                message.sqlQueries.length > 0 && (
                  <SQLDisplay queries={message.sqlQueries} />
                )}

              {/* Display charts if present */}
              {message.type === "received" &&
                message.charts &&
                message.charts.length > 0 && (
                  <ChartDisplay charts={message.charts} />
                )}

              {/* Display structured non-SQL tables if present */}
              {message.type === "received" &&
                message.genericTables &&
                message.genericTables.length > 0 && (
                  <GenericTableDisplay tables={message.genericTables} />
                )}

              {/* Display agent trace (inline, collapsed by default) */}
              {message.type === "received" && (
                <AgentTraceDisplay message={message} />
              )}

              <Typography
                variant="caption"
                className="message-timestamp"
                sx={{
                  mt: 1,
                  opacity: 0,
                  display: "block",
                  transition: "opacity 0.2s ease",
                  fontSize: "0.7rem",
                  color: "text.secondary",
                }}
              >
                {formatMessageTimestamp(message.timestamp)}
              </Typography>
            </Box>
          </Box>
        );
      })}
      {/* Show AgentTraceDisplay when streaming, otherwise show dots if awaiting */}
      {agentState.status === "streaming" ? (
        <>
          {agentState.streamedText && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
              <Box
                sx={{
                  maxWidth: "70%",
                  minWidth: "100px",
                  p: 2,
                  px: 2.5,
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: CHAT_FONT_STACK,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {agentState.streamedText}
                </Typography>
              </Box>
            </Box>
          )}
          <AgentTraceDisplay isStreaming />
        </>
      ) : (
        isAwaitingResponse && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
            <Box
              sx={{
                maxWidth: "70%",
                minWidth: "100px",
                p: 2,
                borderRadius: "20px",
                // Glassmorphism to match AI messages
                background: "rgba(255, 255, 255, 0.7)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <CustomAwaitingResponse />
            </Box>
          </Box>
        )
      )}
      <div ref={messagesEndRef} />
    </>
  );
};

export const ChatAssistantContainer = () => {
  const {
    connectChat,
    disconnectChat,
    sendMessage,
    queuedMessages,
    queuedPrompts,
    isStopRequested,
    isConnected,
    chatSessionId,
    messages,
    isAwaitingResponse,
    agentState,
  } = useContext(ChatContext);
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);

  const quickActionsDisabled =
    isAwaitingResponse || agentState.status === "streaming";
  const showJumpToLatest =
    !isPinnedToBottom &&
    (agentState.status === "streaming" || isAwaitingResponse || messages.length > 0);

  const showWelcomeScreen =
    messages.length <= 1 && !isAwaitingResponse && agentState.status === "idle";

  const handleChatScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsPinnedToBottom(distanceFromBottom < 80);
  };

  const jumpToLatest = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setIsPinnedToBottom(true);
  };

  // Check if there are any runs available
  const hasAnyRuns = mainRun || lastSuccessfulRun;

  // Auto-start chat session when component mounts and we have a run
  useEffect(() => {
    if (hasAnyRuns && !isConnected) {
      const run = mainRun || lastSuccessfulRun;
      connectChat(run, "onboarding");
    }
  }, [hasAnyRuns, isConnected, mainRun, lastSuccessfulRun, connectChat]);

  // Browser navigation is now handled by the useGuardedNavigation hook

  // Disconnect chat session when component unmounts
  useEffect(() => {
    return () => {
      if (isConnected && chatSessionId) {
        disconnectChat();
      }
    };
  }, [isConnected, chatSessionId, disconnectChat]);

  const mainContent = hasAnyRuns ? (
    showWelcomeScreen ? (
      <WelcomeScreen />
    ) : (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 1, pb: 1 }}>
            <Box
              sx={{
                p: 1.25,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: "rgba(25, 118, 210, 0.18)",
                backgroundColor: "rgba(25, 118, 210, 0.05)",
              }}
            >
              <Typography
                sx={{
                  fontFamily: CHAT_FONT_STACK,
                  fontSize: "0.8rem",
                  color: "text.secondary",
                }}
              >
                Nucleus combines live tools, SQL, and docs in one answer. Use
                quick actions for structured outputs.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
            <Box
              ref={scrollContainerRef}
              onScroll={handleChatScroll}
              sx={{
                height: "100%",
                overflowY: "auto",
                overflowAnchor: "none",
                display: "flex",
                flexDirection: "column",
                p: 1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "3px",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.3)",
                  },
                },
              }}
            >
              <CustomMessageContainer
                scrollContainerRef={scrollContainerRef}
                shouldAutoScroll={isPinnedToBottom}
              />
            </Box>
            {showJumpToLatest && (
              <Button
                size="small"
                variant="contained"
                onClick={jumpToLatest}
                sx={{
                  position: "absolute",
                  right: 20,
                  bottom: 12,
                  borderRadius: "999px",
                  textTransform: "none",
                  boxShadow: "0 6px 18px rgba(25, 118, 210, 0.25)",
                }}
              >
                Jump to latest
              </Button>
            )}
          </Box>

          <Box
            sx={{
              py: 1.5,
              px: 1,
              display: "flex",
              width: "100%",
            }}
          >
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
              <InputContainer />
              {queuedPrompts.length > 0 && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: "rgba(25, 118, 210, 0.24)",
                    backgroundColor: "rgba(25, 118, 210, 0.05)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "text.secondary",
                      mb: 0.75,
                    }}
                  >
                    Queued follow-ups ({queuedPrompts.length})
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {queuedPrompts.map((prompt, idx) => (
                      <Chip
                        key={`${idx}-${prompt}`}
                        size="small"
                        label={`${idx + 1}. ${prompt}`}
                        sx={{
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "420px",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <QuickActionRow
                compact
                disabled={quickActionsDisabled}
                onSelect={sendMessage}
              />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "text.secondary",
                  pl: 0.5,
                }}
              >
                {isStopRequested
                  ? "Stopping current response after this safe checkpoint..."
                  : queuedMessages > 0
                    ? `Queued follow-ups: ${queuedMessages}`
                    : "Enter to send · Shift+Enter for newline"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  ) : (
    <NullStateMessage>
      Please make a first run before attempting to view run data.
    </NullStateMessage>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Centered Header with Rotating Logo */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          position: "relative",
          py: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Constantly rotating Atomic logo */}
          <Box
            sx={{
              animation: "spin 8s linear infinite",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "@keyframes spin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          >
            <AtomicLogo width={28} height={28} />
          </Box>
          <Typography
            variant="h1"
            sx={{
              fontFamily: CHAT_FONT_STACK,
              fontWeight: 600,
              fontSize: "1.4rem",
              letterSpacing: "-0.02em",
            }}
          >
            Nucleus
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: "primary.light",
              color: "primary.dark",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 500,
              fontSize: "0.7rem",
              fontFamily: CHAT_FONT_STACK,
            }}
          >
            Beta
          </Typography>
        </Box>
        {/* Status indicator positioned absolutely on the right */}
        <Box sx={{ position: "absolute", right: 8 }}>
          <ChatStatus isConnected={isConnected} />
        </Box>
      </Box>
      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {mainContent}
      </Box>
    </Box>
  );
};
